import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../auth/AuthContext";
import {
  applyVinFields,
  isCompleteVin,
  vinDecodeErrorMessage,
} from "./vinAutofill";

/**
 * Autofill listing form fields from NHTSA vPIC when VIN reaches 17 chars.
 * @param {{ enabled?: boolean, vin: string, setForm: Function, skipExistingVin?: boolean }} opts
 */
export function useVinDecodeAutofill({
  enabled = true,
  vin,
  setForm,
  /** When true, treat the first complete VIN as already handled (edit-page load). */
  skipExistingVin = false,
}) {
  const [decoding, setDecoding] = useState(false);
  const [filledVin, setFilledVin] = useState("");
  const attemptedRef = useRef("");
  const seededRef = useRef(false);
  const abortRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!skipExistingVin || seededRef.current || !enabled) return;
    const normalized = String(vin || "").trim().toUpperCase();
    if (!isCompleteVin(normalized)) return;
    attemptedRef.current = normalized;
    seededRef.current = true;
  }, [skipExistingVin, enabled, vin]);

  const decodeVin = useCallback(async (rawVin, { silent, force } = {}) => {
    const normalized = String(rawVin || "").trim().toUpperCase();
    if (!enabled || !isCompleteVin(normalized)) return null;
    if (!force && attemptedRef.current === normalized) return null;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++seqRef.current;
    attemptedRef.current = normalized;
    seededRef.current = true;
    setDecoding(true);
    try {
      const { data } = await axios.get(
        `${API}/vehicles/vin/decode/${encodeURIComponent(normalized)}`,
        { withCredentials: true, signal: controller.signal },
      );
      if (seq !== seqRef.current) return null;
      const fields = data?.fields || {};
      setForm((f) => (f ? applyVinFields(f, fields) : f));
      setFilledVin(normalized);
      const label = [fields.year, fields.make, fields.model].filter(Boolean).join(" ");
      if (!silent) {
        toast.success(label
          ? `Filled from VIN: ${label}. Review before submitting.`
          : "Vehicle details filled from VIN. Review before submitting.");
      }
      return data;
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
        return null;
      }
      if (seq !== seqRef.current) return null;
      if (!silent) toast.error(vinDecodeErrorMessage(e.response?.data?.detail));
      return null;
    } finally {
      if (seq === seqRef.current) setDecoding(false);
    }
  }, [enabled, setForm]);

  useEffect(() => {
    if (!enabled || !isCompleteVin(vin)) return undefined;
    const normalized = String(vin).trim().toUpperCase();
    if (attemptedRef.current === normalized) return undefined;
    const t = setTimeout(() => {
      decodeVin(normalized, { silent: false });
    }, 400);
    return () => clearTimeout(t);
  }, [vin, enabled, decodeVin]);

  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const onVinConfirmed = useCallback((confirmed) => {
    const normalized = String(confirmed || "").trim().toUpperCase();
    setForm((f) => (f ? { ...f, vin: normalized } : f));
    decodeVin(normalized, { silent: false, force: true });
  }, [decodeVin, setForm]);

  return { decoding, decodeVin, onVinConfirmed, filledVin };
}
